/**
 * `PageListLayout` — the shared frame for the list pages that have not yet been rebuilt on top of
 * `CordioAbpPagedDataTable` (WebAutomationRuns, RpaSystemsList, MemoryQuarantine, MemoryRules,
 * MemoryPipeline, EventOpsRulesList, EmailTemplates, ApiExplorer).
 *
 * The **public shape has not changed** — every consumer still passes `title`, `subtitle`,
 * `breadcrumbs`, `createButton`, `filters`, `viewMode`, `activeView`, `onViewChange`, `children`,
 * `pagination`, `loading`, `error`, `emptyState`, `isEmpty`, `onErrorDismiss`, `headerActions`. The
 * MUI-based glassmorphism internals have been swapped out for `@coordio/ui`'s list-page primitives
 * (`ShellHeader`, `Button`, `LoadingSkeleton`, `EmptyState`, `InlineErrorPanel`) so every page
 * still on this wrapper inherits the CoOrdio Cortex shell chrome without needing to be touched.
 *
 * Pages that have already been rebuilt on top of `CordioAbpPagedDataTable` (see
 * `ProjectListPage.tsx` and the 26 sister list pages migrated in commit `108c83c`) do **not** go
 * through this wrapper — they own their own `ShellHeader` + grid + `DeleteDialog` composition.
 * This file is the transitional home for the pages whose data shape (array-of-templates,
 * cross-service composites) does not fit the paged data table.
 *
 * ─── Pagination note ───────────────────────────────────────────────────────────────────────────
 * MUI's `Pagination` component was the one MUI-only visual in the original layout. It is replaced
 * here by a compact prev/next row plus a "Page X of Y" indicator. The `PaginationConfig` public
 * shape (`page`, `totalPages`, `onChange`) is unchanged, so consumers do not need to know.
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LayoutGrid, Rows } from 'lucide-react';

import { Button, ShellHeader } from '@coordio/ui';
import {
  EmptyState,
  InlineErrorPanel,
  LoadingSkeleton,
  PageContainer,
} from '@coordio/ui/layout';

import './PageListLayout.scss';

// ─── Public API — do not change without a migration of every consumer ─────────────────────────────

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface CreateButtonConfig {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
}

interface PaginationConfig {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

interface EmptyStateConfig {
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface PageListLayoutProps {
  /** Page title. */
  title: string;
  /** Optional page subtitle. */
  subtitle?: string;
  /** Breadcrumb navigation items. */
  breadcrumbs?: BreadcrumbItem[];
  /** Create/Add button configuration. */
  createButton?: CreateButtonConfig;
  /** Filter bar content (rendered inside a bordered card). */
  filters?: ReactNode;
  /** View mode toggle: 'grid' | 'table' | 'both'. */
  viewMode?: 'grid' | 'table' | 'both';
  /** Current active view when viewMode is 'both'. */
  activeView?: 'grid' | 'table';
  /** Callback when view mode changes. */
  onViewChange?: (view: 'grid' | 'table') => void;
  /** Main content area. */
  children: ReactNode;
  /** Pagination configuration. */
  pagination?: PaginationConfig;
  /** Loading state — renders skeleton rows. */
  loading?: boolean;
  /** Error message. */
  error?: string;
  /** Empty state configuration. */
  emptyState?: EmptyStateConfig;
  /** Whether to show the empty state. */
  isEmpty?: boolean;
  /** Error dismiss handler. */
  onErrorDismiss?: () => void;
  /** Extra actions to render in the header area. */
  headerActions?: ReactNode;
  /** Test ID for the list wrapper. */
  'data-testid'?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────────────────────────

export const PageListLayout = ({
  title,
  subtitle,
  breadcrumbs,
  createButton,
  filters,
  viewMode,
  activeView = 'table',
  onViewChange,
  children,
  pagination: paginationConfig,
  loading = false,
  error,
  emptyState,
  isEmpty = false,
  onErrorDismiss,
  headerActions,
  'data-testid': testId,
}: PageListLayoutProps) => {
  const navigate = useNavigate();

  // ─── Header actions: [extras] [view toggle] [create button] ──────────────────────────────────
  // `ShellHeader.actions` takes a single node, so the extras/view-toggle/create-button are
  // composed here into one row.
  const actions = (
    <div className="page-list-layout__actions">
      {headerActions}
      {viewMode === 'both' && onViewChange && (
        <div className="page-list-layout__view-toggle" role="group" aria-label="View mode">
          <Button
            type="button"
            variant={activeView === 'grid' ? 'default' : 'outline'}
            size="sm"
            className="page-list-layout__view-btn"
            onClick={() => onViewChange('grid')}
            aria-pressed={activeView === 'grid'}
            aria-label="Grid view"
          >
            <LayoutGrid className="h-4 w-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant={activeView === 'table' ? 'default' : 'outline'}
            size="sm"
            className="page-list-layout__view-btn"
            onClick={() => onViewChange('table')}
            aria-pressed={activeView === 'table'}
            aria-label="Table view"
          >
            <Rows className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      )}
      {createButton && (
        <Button
          type="button"
          variant="default"
          size="sm"
          className="gap-2"
          onClick={createButton.onClick}
        >
          {createButton.icon}
          {createButton.label}
        </Button>
      )}
    </div>
  );

  return (
    <div className="page-list-layout" data-testid={testId}>
      {/* Breadcrumbs sit above the header — same visual order as before, but as their own strip
          rather than inside a scaffolded Box. */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="page-list-layout__breadcrumbs" aria-label="Breadcrumb">
          <ol>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return (
                <li key={crumb.label} className="page-list-layout__crumb">
                  {isLast || !crumb.href ? (
                    <span aria-current={isLast ? 'page' : undefined}>{crumb.label}</span>
                  ) : (
                    <button
                      type="button"
                      className="page-list-layout__crumb-link"
                      onClick={() => crumb.href && navigate(crumb.href)}
                    >
                      {crumb.label}
                    </button>
                  )}
                  {!isLast && (
                    <ChevronRight className="page-list-layout__crumb-sep" aria-hidden />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      <ShellHeader
        title={title}
        description={subtitle}
        actions={
          headerActions || (viewMode === 'both' && onViewChange) || createButton
            ? actions
            : undefined
        }
      />

      <PageContainer>
        {error && (
          <div className="page-list-layout__error">
            <InlineErrorPanel
              message={error}
              data-testid={testId ? `${testId}-error` : undefined}
              action={
                onErrorDismiss ? (
                  <Button type="button" variant="ghost" size="sm" onClick={onErrorDismiss}>
                    Dismiss
                  </Button>
                ) : undefined
              }
            />
          </div>
        )}

        {filters && <div className="page-list-layout__filters">{filters}</div>}

        {loading ? (
          <LoadingSkeleton rows={6} data-testid={testId ? `${testId}-loading` : undefined} />
        ) : isEmpty && emptyState ? (
          <EmptyState
            title={emptyState.title}
            description={emptyState.description}
            action={
              emptyState.action ? (
                <Button type="button" variant="default" size="sm" onClick={emptyState.action.onClick}>
                  {emptyState.action.label}
                </Button>
              ) : undefined
            }
          />
        ) : (
          children
        )}

        {/* Prev/next replaces MUI Pagination. `page` is 1-indexed to match the old public shape
            (`onChange` receives the new 1-indexed page). Nothing is rendered when the caller does
            not opt in via `paginationConfig`. */}
        {paginationConfig && paginationConfig.totalPages > 1 && (
          <div className="page-list-layout__pagination">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => paginationConfig.onChange(paginationConfig.page - 1)}
              disabled={paginationConfig.page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
              Previous
            </Button>
            <span className="page-list-layout__pagination-indicator" aria-live="polite">
              Page {paginationConfig.page} of {paginationConfig.totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => paginationConfig.onChange(paginationConfig.page + 1)}
              disabled={paginationConfig.page >= paginationConfig.totalPages}
              aria-label="Next page"
            >
              Next
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        )}
      </PageContainer>
    </div>
  );
};

export default PageListLayout;
